import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useContext } from "react";
import { AuthContext } from "@bw/shared/hooks";
import { facilityApi } from "@bw/shared/api";
import type {
  CartQuoteResponse,
  ReservationQuoteRequest,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import type { CartSlot } from "../state/cartStore";

/**
 * The BINDING multi-slot cart quote (POST /facilities/{id}/cart-quote, #1312).
 *
 * Each cart slot maps to a ReservationQuoteRequest carrying the SAME parameters
 * the booking would charge under (player_count = the chosen group size, non_member
 * 0, court_type/player_category "any") — so the returned per-slot + total numbers
 * are the real price, not the 3a client-side per-cell estimate. The estimate is
 * deleted on the review screen entirely; this quote is the only number allowed
 * there.
 *
 * `playerCount` is the SINGLE booking-level group size (slice ② allocation),
 * applied to EVERY slot so each court is priced for the same group — its
 * `extra_player` surcharge (when the count exceeds the included count) is the
 * backend's number, surfaced verbatim. It defaults to 1 (today's behavior) when
 * the facility allocation flag is off or the host never touches the stepper.
 *
 * `timezone` is threaded from the facility so a non-UTC venue is not mispriced by
 * the request's UTC default. The query is keyed by the slot set + tz + the group
 * count, campaign, and authenticated payer. Only the server determines offer
 * eligibility and discounted totals. A captured payment attempt disables fresh
 * quoting because retries use its immutable request. The backend 409s on a mixed-currency cart — that ApiError surfaces on
 * `query.error` for the page to map to an honest "your cart mixes currencies"
 * state.
 */
export function slotToQuoteRequest(
  slot: CartSlot,
  timezone: string,
  playerCount = 1,
): ReservationQuoteRequest {
  return {
    court_id: slot.court_id,
    start_time: slot.start_time,
    end_time: slot.end_time,
    timezone,
    player_count: playerCount,
    non_member_player_count: 0,
    court_type: "any",
    player_category: "any",
    // A booking/cart quote never needs the rule trace — only the operator
    // pricing editor requests explain:true. Keep this off so the quote stays
    // a plain price, not a debug trace.
    explain: false,
  };
}

/** Stable identity for the cart, so the quote re-fetches only on a real change. */
function slotKeys(slots: CartSlot[]): string[] {
  return slots.map((s) => `${s.court_id}|${s.start_time}`);
}

export function useCartQuote(
  facilityId: string | undefined,
  slots: CartSlot[],
  // Undefined until the facility timezone resolves. The quote MUST NOT fire with
  // a fallback tz — a non-UTC/non-Pacific venue would be mispriced, and since the
  // key now includes tz it would otherwise be a separate (wrong) cache entry.
  timezone: string | undefined,
  // The single booking-level group size, applied to every slot (slice ②).
  // Defaults to 1 (today's behavior). Part of the cache key so raising the count
  // refetches the binding price, exactly as a changed cart or tz does.
  playerCount = 1,
  campaignId: string | null = null,
  skipPromotions = false,
  enabled = true,
) {
  const auth = useContext(AuthContext);
  return useQuery<CartQuoteResponse>({
    queryKey: queryKeys.booking.quote(
      facilityId ?? "",
      slotKeys(slots),
      timezone ?? "",
      playerCount,
      campaignId,
      skipPromotions,
      auth?.user?.uid ?? null,
    ),
    queryFn: () =>
      facilityApi.cartQuote(facilityId as string, {
        ...(campaignId ? { campaign_id: campaignId } : {}),
        skip_promotions: skipPromotions,
        slots: slots.map((s) =>
          slotToQuoteRequest(s, timezone as string, playerCount),
        ),
      }),
    // Never quote an empty cart, or before the venue tz is known.
    enabled:
      enabled && Boolean(facilityId) && slots.length > 0 && Boolean(timezone),
    // Keep the prior quote visible while a count change (slice ②) re-fetches the
    // new price — so the breakdown + Continue bar never blink out mid-flow (which
    // would also unmount the stepper the host is clicking). The new server total
    // replaces it once it resolves.
    placeholderData: keepPreviousData,
    retry: false,
  });
}
